import React, { useCallback, useEffect, useState } from "react"
import { ThrowError } from "../errors";

export enum SwipeDirection {
    Up = 0,
    Down = 1,
    Left = 2,
    Right = 3,
}

interface ISwipeDetectionProps {
    x0: number;
    y0: number;
    xF: number;
    yF: number;
    moved: boolean;
}

const minimumSwipeDistance = 300;

export const useSwipeDetection = (onSwipe: (direction: SwipeDirection) => void) => {
	const [ swipeProps, setSwipeProps ] = useState<ISwipeDetectionProps>();
	
	useEffect(() => {
		setSwipeProps({
			x0: 0,
			y0: 0,
			xF: 0,
			yF: 0,
			moved: false
		});
	}, []);

	const onTouchStart = useCallback((event: React.TouchEvent) => {
		const touch = event.touches[0];
		setSwipeProps({
			x0: touch.screenX,
			y0: touch.screenY,
			xF: 0,
			yF: 0,
			moved: false
		});
	}, []);

	const onTouchMove = useCallback((event: React.TouchEvent) => {
		const touch = event.touches[0];
		setSwipeProps(oldSwipe => ({
			x0: oldSwipe?.x0 ?? 0,
			y0: oldSwipe?.y0 ?? 0,
			xF: touch.screenX,
			yF: touch.screenY,
			moved: true
		}));
	}, []);

	const onTouchEnd = useCallback(() => {
		if (!swipeProps || !swipeProps.moved) { return; }
		const dX = swipeProps.xF - swipeProps.x0;
		const dY = swipeProps.yF - swipeProps.y0;

		if (dX ** 2 + dY ** 2 < minimumSwipeDistance ** 2) {
			return;
		}

		let direction: SwipeDirection | null = null;

		// horizontal
		if (dY === 0 || Math.abs(dX / dY) > 1) {
			direction = (dX > 0) ? SwipeDirection.Right : SwipeDirection.Left;
		}
		// vertical
		else {
			direction = (dY > 0) ? SwipeDirection.Down : SwipeDirection.Up;
		}

		onSwipe(direction);
	}, [onSwipe, swipeProps]);

	return {
		onTouchStart: onTouchStart,
		onTouchMove: onTouchMove,
		onTouchEnd: onTouchEnd,
	};
}